import { Drawer } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import closeIcon from '@/shared/ui/icons/Components/Common/close-icon.svg';
import coloredAddIcon from '@/shared/ui/icons/Components/Actions/colored-add-icon.svg';
import editIcon from '@/shared/ui/icons/Tables/colored-edit-icon.svg';
import { FACILITY_NOTES_MAX_CHAR, auth } from '@/shared/lib';
import { IconButton, ConfirmModal, EditModal } from '@/shared/ui';
import useFacilityInfoDrawer from './useFacilityInfoDrawer';

export type FacilityInfoDrawerProps = {
  visible: boolean;
  title: string;
  facilityId: string;
  handleClickCloseButton: () => void;
  isAbortedStudy?: boolean;
};

const FacilityInfoDrawer = ({
  visible,
  title,
  facilityId,
  handleClickCloseButton,
  isAbortedStudy = false,
}: FacilityInfoDrawerProps) => {
  const {
    state,
    toggleDisplayEditModal,
    updateFacilityNote,
    onClickSaveClinicianContact,
    toggleDisplayConfirmDeleteClinicianContactModal,
    updateFacilityClinicianContacts,
    handleScroll,
    onChangeFacilityNote,
    generateInputList,
    onChangeClinicianContact,
  } = useFacilityInfoDrawer({
    visible,
    facilityId,
    handleClickCloseButton,
    isAbortedStudy,
  });

  return (
    <>
      <EditModal
        className="custom-edit-modal"
        oneInputPerLine
        isOpen={state.isOpenEditModal}
        toggle={toggleDisplayEditModal}
        header={
          state.selectedEditName === 'Facility Notes'
            ? state.selectedEditName
            : state.isUpdateClinicianContact
              ? 'Edit Clinic Support Contact'
              : 'Add Clinic Support Contact'
        }
        isTextareaInput={state.selectedEditName === 'Facility Notes'}
        inputList={generateInputList()}
        onClickSave={
          state.selectedEditName === 'Facility Notes'
            ? updateFacilityNote
            : onClickSaveClinicianContact
        }
        onChange={
          state.selectedEditName === 'Facility Notes'
            ? onChangeFacilityNote
            : onChangeClinicianContact
        }
        isSaving={state.isOpenConfirmDeleteClinicianContactModal ? false : state.isSaving}
        maxChar={state.selectedEditName === 'Facility Notes' ? FACILITY_NOTES_MAX_CHAR : undefined}
        hasLeftButton={
          state.selectedEditName !== 'Facility Notes' && state.isUpdateClinicianContact
        }
        leftButtonName="Delete Contact"
        onClickLeftButton={toggleDisplayConfirmDeleteClinicianContactModal}
        buttonName={
          state.selectedEditName === 'Facility Notes'
            ? 'Save'
            : state.isUpdateClinicianContact
              ? 'Save Changes'
              : 'Add Contact'
        }
      />

      <ConfirmModal
        isSaving={state.isSaving}
        isOpen={state.isOpenConfirmDeleteClinicianContactModal}
        onClickLeftButton={toggleDisplayConfirmDeleteClinicianContactModal}
        onClickRightButton={() => {
          updateFacilityClinicianContacts(true);
        }}
        leftButtonName="Cancel"
        rightButtonName="Delete"
        title="Delete clinic support contact"
        question="Are you sure you want to delete this clinic support contact? This action can not be undone."
      />

      <Drawer
        className="custom-drawer"
        width={450}
        closable={false}
        onClose={handleClickCloseButton}
        open={visible}
        aria-label={title}
      >
        <div
          className={classnames('drawer-title-container', state.isScroll ? '--has-box-shadow' : '')}
        >
          <span className="drawer-title">{title}</span>
          <IconButton
            isFilled
            iconComponent={<img src={closeIcon} alt="Close icon" />}
            onClick={handleClickCloseButton}
          />
        </div>

        <div
          className={classnames(
            'drawer-body-container',
            auth.isCallCenter() ? 'style-callcenter' : '',
          )}
          onScroll={handleScroll}
        >
          {_.map(state.data, (element: any, index: number) => {
            if (element.header === 'Facility Notes') {
              return (
                <div key={index} className="drawer-body">
                  {!!element.header && (
                    <div className="drawer-header">
                      <span>{element.header}</span>
                      <IconButton
                        id="facility-notes-edit-btn"
                        iconComponent={<img src={editIcon} alt="Edit icon" />}
                        disabled={element.isNotEdit}
                        onClick={element.handleClickEdit}
                      />
                      {element.isNotEdit && (
                        <UncontrolledTooltip
                          className="custom-tooltip"
                          target="facility-notes-edit-btn"
                          placement="bottom-end"
                          delay={{ show: 0, hide: 0 }}
                          hideArrow
                        >
                          Unable to edit facility notes
                        </UncontrolledTooltip>
                      )}
                    </div>
                  )}

                  <div className="drawer-content">
                    <div className="notes">{element.content}</div>
                  </div>
                </div>
              );
            }

            if (element.header === 'Clinic Support Contacts') {
              return (
                <div key={index} className="drawer-body">
                  {!!element.header && (
                    <div className="drawer-header">
                      <span>{element.header}</span>

                      <IconButton
                        className="add-icon"
                        iconComponent={<img src={coloredAddIcon} alt="Add icon" />}
                        disabled={element.isNotEdit}
                        onClick={() => {
                          element.handleClickEdit(state.clinicianContacts);
                        }}
                      />
                    </div>
                  )}

                  <div className="drawer-content">
                    {_.map(element.content, (item: any, index: number) => (
                      <div className="clinician-contact-item">
                        <div className="order">
                          <strong>{`#${index + 1}`}</strong>
                          <IconButton
                            className="edit-icon"
                            iconComponent={<img src={editIcon} alt="Edit icon" />}
                            disabled={element.isNotEdit}
                            onClick={() => {
                              element.handleClickEdit(state.clinicianContacts, index);
                            }}
                          />
                        </div>
                        <div key={index} className="drawer-item">
                          <span>Name:</span>
                          <span>{item.name || '--'}</span>
                        </div>
                        <div key={index} className="drawer-item">
                          <span>Phone Number:</span>
                          <span>{item.phone}</span>
                        </div>
                        <div key={index} className="drawer-item">
                          <span>Email:</span>
                          <span>{item.email}</span>
                        </div>
                        <div key={index} className="drawer-item">
                          <span>Description:</span>
                          <span>{item.description || '--'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className="drawer-body">
                {!!element.header && (
                  <div className="drawer-header">
                    <span>{element.header}</span>
                  </div>
                )}

                <div className="drawer-content">
                  {_.map(element.content, (item: any, index: number) => (
                    <div key={index} className="drawer-item">
                      <span>{`${item.label}:`}</span>
                      <span>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="additional-div" />
        </div>
      </Drawer>
    </>
  );
};

export default FacilityInfoDrawer;
